import { Component, OnInit, Input, OnChanges, SimpleChange } from '@angular/core';
import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { RankingsService } from 'app/modules/performance/services/rankings.service';
import { Router } from '@angular/router';
import {
    NoDataTypes,
    NoDataActionType,
    NoDataSourceType,
} from 'app/modules/shared/interfaces/no-data';
import { EntityLeaderboardService } from 'app/modules/performance/services/entity-leaderboard.service';
import { IWeightedScoreData } from 'app/modules/performance/interfaces/performance-entity-data';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';

export interface IDataMappingProperties {
    unitsMappingProperty: string;
    volumeMappingProperty: string;
    scoreMappingProperty: string;
    nameMappingProperty: string;
}

export interface IDataProperties {
    data: IWeightedScoreData[];
    isLoading: boolean;
    hasData: boolean;
    tableTitle: string;
    tablePageSize: number;
    roleId: string;
    action: NoDataActionType;
    showFooter: boolean;
}

export interface IWeightedScoreTable {
    navigationParams: INavigationParameters;
    dataMappingProperties: IDataMappingProperties;
    dataProperties: IDataProperties;
}

export interface INavigationParameters {
    titleRouterBase: string;
    titleUrlSegment: string;
    cellRouterBase: string;
    cellUrlSegment: string;
    cellQueryParamsList: Array<{ param: string; valueId: string; source: 'cell' | 'parent' }>;
}

@Component({
    selector: 'performance-insight-ranking-table',
    templateUrl: './performance-insight-ranking-table.component.html',
    styleUrls: ['./performance-insight-ranking-table.component.scss'],
})
export class PerformanceInsightRankingTableComponent implements OnInit, OnChanges {
    gridView: GridDataResult;
    skip = 0;
    sortable: object = {
        mode: 'single',
        allowUnsort: true,
    };
    scrollable: 'scrollable' = 'scrollable';
    type: 'numeric' | 'input' = 'input';
    rankingData: IWeightedScoreData[];
    noDataType: NoDataTypes;
    rankEntrySort: SortDescriptor[];
    volTotal: number = 0;
    unitTotal: number = 0;
    @Input() navigationParams: INavigationParameters;
    @Input() dataMappingProperties: IDataMappingProperties;
    @Input() dataProperties: IDataProperties;

    constructor(
        private rankingsService: RankingsService,
        private entityLeaderboardService: EntityLeaderboardService,
        private router: Router,
        private _reusableFunctions: ReusableFunctions
    ) {}

    ngOnInit() {
        scroll(0, 0);
        this.rankEntrySort = [];

        this.noDataType = {
            action: NoDataActionType.pageStart,
            searchTerm: '',
            componentTitle: this.dataProperties.tableTitle,
            inputSource: NoDataSourceType.table,
        };

        this.rankingData = [];
    }

    ngOnChanges(changes: { [property: string]: SimpleChange }) {
        let change: SimpleChange = changes['dataProperties'];
        if (change) {
            this.processData();
        }
    }

    processData() {
        if (this.dataProperties && this.noDataType) {
            this.noDataType.action = this.dataProperties.action;
        }

        let data = this.dataProperties.data;
        if (data && data.length > 0) {
            let properties = this.dataMappingProperties;
            this.rankingData = data.filter((item) => item.isCurrentPeriod === true);
            this.rankingData.map((dat) => {
                dat.displayUnits = dat[properties.unitsMappingProperty];
                dat.displayVolume = dat[properties.volumeMappingProperty];
                dat.displayName = dat[properties.nameMappingProperty];
                dat.displayScore = dat[properties.scoreMappingProperty];
            });

            this.rankingData.sort((a, b) => b.displayScore - a.displayScore);

            this.volTotal = 0;
            this.unitTotal = 0;
            let volumeMax = this._reusableFunctions.getMaxValue(this.rankingData, 'displayVolume');
            let unitsMax = this._reusableFunctions.getMaxValue(this.rankingData, 'displayUnits');

            for (let ranking of this.rankingData) {
                this.volTotal = this.volTotal + ranking.displayVolume;
                this.unitTotal += ranking.displayUnits;
                ranking.volumePct = (ranking.displayVolume / volumeMax) * 100;
                ranking.unitsPct = (ranking.displayUnits / unitsMax) * 100;
            }
        }
        this.updateGrid();
    }

    updateGrid() {
        let orderedData = [];
        if (this.rankingData) {
            orderedData = orderBy(this.rankingData, this.rankEntrySort);
        }

        this.gridView = {
            data: orderedData.slice(this.skip, this.skip + this.dataProperties.tablePageSize),
            total: orderedData.length,
        };
    }

    sortChange(sort: SortDescriptor[]) {
        this.rankEntrySort = sort;
        this.updateGrid();
    }

    clickHandler() {
        this.router.navigate(
            [this.navigationParams.titleRouterBase, this.navigationParams.titleUrlSegment],
            {
                queryParams: {},
                state: { role: this.dataProperties.roleId },
            }
        );
    }

    cellClickHandler({ dataItem, type }) {
        let queryParams = {};
        this.navigationParams.cellQueryParamsList.forEach((param) => {
            if (param.source === 'cell') {
                queryParams[param.param] = dataItem[param.valueId];
            } else {
                queryParams[param.param] = param.valueId;
            }
        });

        this.router.navigate(
            [this.navigationParams.cellRouterBase, this.navigationParams.cellUrlSegment],
            {
                queryParams: queryParams,
                state: { role: this.dataProperties.roleId },
            }
        );
    }
}
